import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Slider,
  Avatar,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Forward30 as Forward30Icon,
  Replay30 as Replay30Icon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { IAudioPlayerDialogProps } from "../types/audio-player";

const AudioPlayerDialog: React.FC<IAudioPlayerDialogProps> = ({
  open,
  onClose,
  audio,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(180);
  const [volume, setVolume] = useState(70);
  const audioRef = useRef<HTMLAudioElement>(null);

  if (!audio) return null;

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSeek = (event: Event, newValue: number | number[]) => {
    setCurrentTime(newValue as number);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      component={motion.div}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Audio Player</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 0 }}>
          {/* Album Art */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Avatar
              src={audio.image}
              sx={{
                width: 200,
                height: 200,
                mx: "auto",
                mb: 2,
                borderRadius: 2,
              }}
            />
            <Typography variant="h6" gutterBottom>
              {audio.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {audio.author}
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            <Slider
              value={currentTime}
              max={duration}
              onChange={handleSeek}
              aria-labelledby="audio-progress-slider"
              sx={{ color: "primary.main" }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="caption">
                {formatTime(currentTime)}
              </Typography>
              <Typography variant="caption">{formatTime(duration)}</Typography>
            </Box>
          </Box>

          {/* Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <IconButton>
              <Replay30Icon />
            </IconButton>
            <IconButton
              onClick={handlePlayPause}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                width: 56,
                height: 56,
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </IconButton>
            <IconButton>
              <Forward30Icon />
            </IconButton>
          </Box>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default AudioPlayerDialog;
